<!--
  Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at
 
     http://www.apache.org/licenses/LICENSE-2.0
 
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
 
   SPDX-License-Identifier: Apache-2.0
-->

<div class="administration-container">

    <div class="administration-head d-flex align-items-center flex-wrap gap-3">

        <div
            class="administration-head__left d-flex flex-wrap align-items-center gap-2"
            [class.administration-head__left--onboarding]="showParticipantsOnboarding"
        >
            <!-- ADD BUTTON -->
            <button
                class="btn btn-primary"
                id="general-btn"
                (click)="openAddParticipant()"
            >
                {{ 'ADMINISTRATION.ADD' | translate }}&nbsp;
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="bi bi-plus-circle-fill" viewBox="0 0 18 18" aria-hidden="true">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8.5 4.5a.5.5 0 0 0-1 0v3h-3a.5.5 0 0 0 0 1h3v3a.5.5 0 0 0 1 0v-3h3a.5.5 0 0 0 0-1h-3z"/>
                </svg>
            </button>

            @if (showParticipantsOnboarding) {
                <div class="administration-add-hint" role="note">
                    <span class="administration-add-hint__arrow" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M12 19V5"/>
                            <path d="m5 12 7-7 7 7"/>
                        </svg>
                    </span>
                    <p class="administration-add-hint__text">{{ 'ADMINISTRATION.ADD-HINT' | translate }}</p>
                </div>
            }
        </div>

        <!-- SEARCH -->
        <div class="administration-search ms-auto" [class.administration-search--disabled]="showParticipantsOnboarding">
            <div class="page-search-field" role="search">
                <span class="page-search-field__lead" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.3-4.3"/>
                    </svg>
                </span>
                <input
                    type="text"
                    class="form-control page-search-field__input"
                    #administrationSearcher
                    (input)="search()"
                    [disabled]="showParticipantsOnboarding"
                    [placeholder]="'ADMINISTRATION.SEARCH' | translate"
                    [attr.aria-label]="'GENERAL.SEARCH' | translate"
                />
                <button
                    type="button"
                    class="page-search-field__reset"
                    (click)="resetSearch()"
                    [disabled]="showParticipantsOnboarding"
                    [attr.aria-label]="'GENERAL.RESET-SEARCH' | translate"
                    [attr.title]="'GENERAL.RESET-SEARCH' | translate"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                        <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
                        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
                    </svg>
                </button>
            </div>
        </div>

    </div>

    <!-- TABLE / empty state -->
    <div class="administration-wrapper">
        @if (showParticipantsOnboarding) {
            <div class="administration-empty-main" role="region" aria-labelledby="administration-onboarding-title">
                <div class="administration-empty-main__card">
                    <div class="administration-empty-main__badge" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" class="bi bi-person" viewBox="0 0 16 16">
                            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.208C11.066 13.357 9.87 13 8 13s-3.066.357-4.168 1.152c-.678.222-.83.962-.832 1.208z"/>
                        </svg>
                    </div>
                    <h2 class="administration-empty-main__title" id="administration-onboarding-title">{{ 'ADMINISTRATION.EMPTY-TITLE' | translate }}</h2>
                    <p class="administration-empty-main__lead">{{ 'ADMINISTRATION.EMPTY-LEAD' | translate }}</p>
                </div>
            </div>
        } @else {
            <div class="administration-body">
                @if (filteredParticipants.length === 0) {
                    <div class="administration-no-results" role="status">
                        <div class="administration-no-results__icon" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="11" cy="11" r="7"/>
                                <path d="m20 20-3.5-3.5"/>
                                <path d="M8 11h6"/>
                            </svg>
                        </div>
                        <h2 class="administration-no-results__title">{{ 'ADMINISTRATION.NO-RESULTS-TITLE' | translate }}</h2>
                        <p class="administration-no-results__lead">{{ 'ADMINISTRATION.NO-RESULTS-LEAD' | translate }}</p>
                        @if (hasSearchTerm()) {
                            <button type="button" class="btn administration-no-results__clear" (click)="resetSearch()">
                                {{ 'ADMINISTRATION.NO-RESULTS-CLEAR' | translate }}
                            </button>
                        }
                    </div>
                } @else {
                <table class="table vertical-align" id="administration-table">

                    <thead>
                        <th
                            class="sortable-th"
                            (click)="sortParticipantsBy('id')"
                            id="table-header"
                            [class.asc]="sortCriteria === 'id' && sortDirection === 'asc'"
                            [class.desc]="sortCriteria === 'id' && sortDirection === 'desc'"
                        >
                            {{ 'ADMINISTRATION.ID' | translate }}
                        </th>
                        <th
                            class="sortable-th"
                            (click)="sortParticipantsBy('name')"
                            id="table-header"
                            [class.asc]="sortCriteria === 'name' && sortDirection === 'asc'"
                            [class.desc]="sortCriteria === 'name' && sortDirection === 'desc'"
                        >
                            {{ 'ADMINISTRATION.NAME' | translate }}
                        </th>
                        <th
                            class="sortable-th"
                            (click)="sortParticipantsBy('targetURL')"
                            id="table-header"
                            [class.asc]="sortCriteria === 'targetURL' && sortDirection === 'asc'"
                            [class.desc]="sortCriteria === 'targetURL' && sortDirection === 'desc'"
                        >
                            {{ 'ADMINISTRATION.TARGET-URL' | translate }}
                        </th>
                        <th>
                            {{ 'ADMINISTRATION.PROTOCOLS' | translate }}
                        </th>
                        <th>
                            {{ 'ADMINISTRATION.ACTIONS' | translate }}
                        </th>
                    </thead>

                    <tbody>
                        @for (participant of filteredParticipants; track participant) {
                            <tr>
                                <td>
                                    <span class="participant-id-cell">
                                        @if (recentlyCreatedParticipantId === participant.ID) {
                                            <span
                                                class="participant-created-indicator"
                                                [attr.title]="'ADMINISTRATION.RECENTLY-CREATED' | translate"
                                                [attr.aria-label]="'ADMINISTRATION.RECENTLY-CREATED' | translate"
                                            ></span>
                                        }
                                        <span>{{ participant.ID }}</span>
                                    </span>
                                </td>
                                <td>{{ participant.name }}</td>
                                <td>{{ participant.targetURL }}</td>
                                <td>{{ participant.supportedProtocols }}</td>
                                <td class="actions-column">
                                    <div class="d-flex justify-content-center gap-2">
                                        <button type="button" class="btn catalog-action-btn catalog-action-btn--danger" (click)="openDeleteParticipant(participant)" data-bs-toggle="tooltip" data-bs-placement="bottom" data-bs-custom-class="custom-tooltip" title="{{ 'ADMINISTRATION.TOOLTIP.DELETE' | translate }}">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="bi bi-trash3-fill catalog-action-icon" viewBox="0 0 16 16" aria-hidden="true">
                                                <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        }
                    </tbody>

                </table>
                }
            </div>
        }
    </div>

    @if (isLoading) {
        <div class="loading-overlay">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    }

</div>

<!----------------------------- MODALS ----------------------------->

<!-- Add Participant -->

<div class="modal fade" #addParticipantModal id="addParticipantModal" tabindex="-1" aria-labelledby="addParticipantModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
	<div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
		<div class="modal-content add-participant-modal__content">
			<div class="modal-header add-participant-modal__header border-0">
				<div class="flex-grow-1 pe-2">
					<h5 class="modal-title title-3 mb-0 text-white" id="addParticipantModalLabel">{{ 'ADMINISTRATION.ADD' | translate }}</h5>
					<p class="add-participant-modal__subtitle mb-0 mt-2">{{ 'ADMINISTRATION.ADD-MODAL-HINT' | translate }}</p>
				</div>
				<button type="button" class="btn-close btn-close-white flex-shrink-0" [attr.aria-label]="'ADMINISTRATION.CANCEL' | translate" (click)="cancelAddParticipant()"></button>
			</div>
			<div class="modal-body add-participant-modal__body">
				<form id="addParticipantModal-form">
					<div class="vstack gap-4">
						<div>
							<h6 class="add-participant-modal__section-title">{{ 'ADMINISTRATION.PROPERTIES' | translate }}</h6>
							<div class="card add-participant-modal__card border-0">
								<div class="card-body p-4">
									<div class="vstack gap-4">
										<div>
											<label for="new-participant-id" class="form-label add-participant-modal__label">
												{{ 'ADMINISTRATION.ID' | translate }}
												<span class="add-participant-modal__required" aria-hidden="true">*</span>
											</label>
											<input
												type="text"
												id="new-participant-id"
												name="new-participant-id"
												class="form-control"
												[placeholder]="'ADMINISTRATION.TOOLTIP.ID' | translate"
												[(ngModel)]="newParticipantID"
												autocomplete="off"
											/>
											@if (wrongNewParticipantIDField) {
												<div class="add-participant-modal__error">{{ 'ADMINISTRATION.ERROR.ID' | translate }}</div>
											}
										</div>
										<div>
											<label for="new-participant-name" class="form-label add-participant-modal__label">
												{{ 'ADMINISTRATION.NAME' | translate }}
												<span class="add-participant-modal__required" aria-hidden="true">*</span>
											</label>
											<input
												type="text"
												id="new-participant-name"
												name="new-participant-name"
												class="form-control"
												[placeholder]="'ADMINISTRATION.TOOLTIP.NAME' | translate"
												[(ngModel)]="newParticipantName"
												autocomplete="off"
											/>
											@if (wrongNewParticipantNameField) {
												<div class="add-participant-modal__error">{{ 'ADMINISTRATION.ERROR.NAME' | translate }}</div>
											}
										</div>
										<div>
											<label for="new-participant-target-url" class="form-label add-participant-modal__label">
												{{ 'ADMINISTRATION.TARGET-URL' | translate }}
												<span class="add-participant-modal__required" aria-hidden="true">*</span>
											</label>
											<input
												type="text"
												id="new-participant-target-url"
												name="new-participant-target-url"
												class="form-control"
												[placeholder]="'ADMINISTRATION.TOOLTIP.TARGET-URL' | translate"
												[(ngModel)]="newParticipantTargetURL"
												autocomplete="off"
											/>
											@if (wrongNewParticipantTargetURLField) {
												<div class="add-participant-modal__error">{{ 'ADMINISTRATION.ERROR.TARGET-URL' | translate }}</div>
											}
										</div>
										<div>
											<label class="form-label add-participant-modal__label">
												{{ 'ADMINISTRATION.PROTOCOLS' | translate }}
												<span class="add-participant-modal__required" aria-hidden="true">*</span>
											</label>
											<div class="multi-select">
												<div class="multi-select-header" (click)="toggleDropdown()">
													<span class="multi-select-header__text text-truncate me-2">
														{{ newParticipantProtocols.length ? newParticipantProtocols.join(', ') : ('ADMINISTRATION.TOOLTIP.PROTOCOL' | translate) }}
													</span>
													<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chevron-down flex-shrink-0" viewBox="0 0 16 16" aria-hidden="true">
														<path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"/>
													</svg>
												</div>
												@if (showProtocolsDropdown) {
													<div class="multi-select-dropdown">
														@for (protocol of protocols; track protocol) {
															<div class="multi-select-option" (click)="toggleProtocol(protocol)">
																<input
																	type="checkbox"
																	class="form-check-input flex-shrink-0 me-2"
																	[checked]="isSelected(protocol)"
																	(change)="toggleProtocol(protocol)"
																/>
																{{ protocol | translate }}
															</div>
														}
													</div>
												}
												@if (wrongNewParticipantProtocolsField) {
													<div class="add-participant-modal__error">{{ 'ADMINISTRATION.ERROR.PROTOCOLS' | translate }}</div>
												}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</form>
			</div>
			<div class="modal-footer add-participant-modal__footer detail-modal__footer border-0 gap-2">
				<button type="button" class="btn btn-outline-light" (click)="cancelAddParticipant()">{{ 'ADMINISTRATION.CANCEL' | translate }}</button>
				<button type="button" class="btn btn-primary" id="addParticipantBtn" (click)="addParticipant()">{{ 'ADMINISTRATION.ADD-SHORT' | translate }}</button>
			</div>
		</div>
	</div>
</div>

<!-- Delete Participant -->

<div class="modal fade" #deleteParticipantModal id="deleteParticipantModal" tabindex="-1" aria-labelledby="deleteParticipantModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
	<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
		<div class="modal-content detail-modal__content">
			<div class="modal-header detail-modal__header border-0">
				<div class="flex-grow-1 pe-2">
					<h5 class="modal-title title-3 mb-0 text-white" id="deleteParticipantModalLabel">{{ 'ADMINISTRATION.DELETE' | translate }}</h5>
					<p class="detail-modal__subtitle mb-0 mt-2">{{ 'ADMINISTRATION.CONFIRMATION' | translate }}</p>
				</div>
				<button type="button" class="btn-close btn-close-white flex-shrink-0" [attr.aria-label]="'ADMINISTRATION.CANCEL' | translate" (click)="cancelDeleteParticipant()"></button>
			</div>
			<div class="modal-body detail-modal__body">
				@if (selectedParticipant) {
					<div class="card border-0 detail-modal__panel">
						<div class="card-body p-3 p-md-4">
							<div class="detail-modal__kv">
								<div class="detail-modal__kv-row">
									<div class="detail-modal__kv-key">{{ 'ADMINISTRATION.ID' | translate }}</div>
									<div class="detail-modal__kv-val text-break">{{ selectedParticipant.ID }}</div>
								</div>
								<div class="detail-modal__kv-row">
									<div class="detail-modal__kv-key">{{ 'ADMINISTRATION.NAME' | translate }}</div>
									<div class="detail-modal__kv-val text-break">{{ selectedParticipant.name }}</div>
								</div>
								<div class="detail-modal__kv-row">
									<div class="detail-modal__kv-key">{{ 'ADMINISTRATION.TARGET-URL' | translate }}</div>
									<div class="detail-modal__kv-val text-break" [attr.title]="selectedParticipant.targetURL">{{ selectedParticipant.targetURL }}</div>
								</div>
								<div class="detail-modal__kv-row">
									<div class="detail-modal__kv-key">{{ 'ADMINISTRATION.PROTOCOLS' | translate }}</div>
									<div class="detail-modal__kv-val text-break">{{ selectedParticipant.supportedProtocols }}</div>
								</div>
							</div>
						</div>
					</div>
				}
			</div>
			<div class="modal-footer detail-modal__footer border-0 gap-2">
				<button type="button" class="btn btn-outline-light" (click)="cancelDeleteParticipant()">{{ 'ADMINISTRATION.CANCEL' | translate }}</button>
				<button type="button" class="btn catalog-danger-text-btn" id="deleteParticipantBtn" (click)="deleteParticipant()">{{ 'ADMINISTRATION.DELETE-SHORT' | translate }}</button>
			</div>
		</div>
	</div>
</div>

<!-- Toast -->

<div class="toast-container position-fixed top-0 end-0 p-3">
	<div
		#toastElement
		class="toast align-items-center text-bg-success border-0"
		role="alert"
		aria-live="assertive"
		aria-atomic="true"
	>
		<div class="d-flex">
			<div class="toast-body">
				{{ toastMessage }}
			</div>
			<button
				type="button"
				class="btn-close btn-close-white me-2 m-auto"
				data-bs-dismiss="toast"
				aria-label="Close">
			</button>
		</div>
	</div>
</div>